/**
 * 🔔 ADMIN NOTIFICATION SERVICE
 * Manages push notifications, templates, and analytics for admin dashboard
 * Backend: https://polarride-api-17dr.vercel.app/api/admin/notifications
 */

import { getApiBaseUrl } from '@/config/environment';


// ========================================
// INTERFACES
// ========================================

export interface NotificationTemplate {
  id: string;
  type: string;
  titleTemplate: string;
  messageTemplate: string;
  variables: string[];
  description: string;
  category: 'vehicle' | 'booking' | 'payment' | 'review' | 'system' | 'marketing';
  isActive: boolean;
  // Multi-channel fields
  priority: 'low' | 'normal' | 'high' | 'urgent';
  channels: {
    push: boolean;
    email: boolean;
    sms: boolean;
  };
  emailSubjectTemplate?: string;
  emailHtmlTemplate?: string;
  smsTemplate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TemplatesResponse {
  success: boolean;
  data: {
    templates: NotificationTemplate[];
    groupedByCategory: Record<string, NotificationTemplate[]>;
    total: number;
  };
  error?: string;
}

export interface UpdateTemplateRequest {
  titleTemplate?: string;
  messageTemplate?: string;
  isActive?: boolean;
  description?: string;
  // Multi-channel fields
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  channels?: {
    push?: boolean;
    email?: boolean;
    sms?: boolean;
  };
  emailSubjectTemplate?: string;
  emailHtmlTemplate?: string;
  smsTemplate?: string;
}

export interface SendNotificationRequest {
  userIds: string[] | 'all_hosts' | 'all_renters';
  title: string;
  message: string;
  type?: string;
  adminUserId: string;
}

export interface SendNotificationResponse {
  success: boolean;
  message: string;
  data: {
    sent: number;
    failed: number;
    total: number;
    sentBy: string;
    sentAt: string;
  };
  error?: string;
}

export interface SendToUserRequest {
  userId?: string;
  email?: string;
  phoneNumber?: string;
  title: string;
  message: string;
  type?: string;
  adminUserId: string;
}

export interface SendToUserResponse {
  success: boolean;
  message: string;
  data: {
    notificationId: string;
    sentTo: {
      userId: string;
      name: string;
      email?: string;
      phoneNumber?: string;
    };
    notification: {
      title: string;
      message: string;
      type: string;
    };
    sentBy: string;
    sentAt: string;
    identifierUsed: 'userId' | 'email' | 'phoneNumber';
  };
  error?: string;
}

export interface UserLookupResponse {
  success: boolean;
  data?: {
    users: Array<{
      userId: string;
      name: string;
      email: string;
      phoneNumber: string;
      role: string;
    }>;
  };
  error?: string;
}

export interface NotificationAnalytics {
  success: boolean;
  data: {
    totalSent: number;
    totalRead: number;
    readRate: number;
    byType: Record<string, number>;
    period: {
      startDate: string;
      endDate: string;
    };
  };
  error?: string;
}

export interface NotificationHistoryItem {
  id: string;
  title: string;
  message: string;
  type: string;
  recipientType: 'specific' | 'all_hosts' | 'all_renters' | 'all';
  recipientIds: string[];
  totalRecipients: number;
  sentCount: number;
  readCount: number;
  failedCount: number;
  sentBy: string;
  sentAt: string;
  createdAt: string;
}

export interface NotificationHistoryResponse {
  success: boolean;
  data: {
    notifications: NotificationHistoryItem[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
      hasMore: boolean;
    };
  };
  error?: string;
}

// Enhanced Stats Interfaces
export interface EnhancedNotificationStats {
  overall: {
    totalSent: number;
    totalRead: number;
    uniqueRecipients: number;
    readRate: number;
  };
  statusBreakdown: {
    sent: number;
    delivered: number;
    failed: number;
  };
  timeRanges: {
    last24h: number;
    last7d: number;
    last30d: number;
  };
  byType: Record<string, {
    total: number;
    read: number;
    readRate: number;
  }>;
  dailyTrend: Array<{
    date: string;
    sent: number;
    read: number;
    readRate: number;
  }>;
  hourlyTrend: Array<{
    hour: string;
    sent: number;
    read: number;
  }>;
  topRecipients: Array<{
    userId: string;
    name: string;
    email: string;
    totalReceived: number;
    totalRead: number;
    readRate: number;
  }>;
  recentNotifications: Array<{
    id: string;
    title: string;
    type: string;
    recipients: number;
    read: number;
    readRate: number;
    sentAt: string;
  }>;
}

export interface NotificationListItem {
  id: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  title: string;
  message: string;
  type: string;
  status: 'sent' | 'delivered' | 'failed' | 'read';
  isRead: boolean;
  readAt?: string;
  sentAt: string;
  createdAt: string;
}

export interface NotificationListResponse {
  success: boolean;
  data: {
    notifications: NotificationListItem[];
    summary: {
      total: number;
      read: number;
      unread: number;
      readRate: number;
    };
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasMore: boolean;
    };
  };
  error?: string;
}

// ========================================
// SERVICE CLASS
// ========================================

class AdminNotificationService {
  private baseUrl = `${getApiBaseUrl()}/api/admin/notifications`;

  /**
   * Helper to get admin user ID from localStorage
   */
  private getAdminUserId(): string {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.id || user.userId || 'admin-default';
      } catch (e) {
      }
    }
    return 'admin-default';
  }

  /**
   * 📋 Get all notification templates
   * GET /api/admin/notifications/templates
   * Optional: ?category=vehicle
   */
  async getTemplates(category?: string): Promise<TemplatesResponse> {
    try {
      const queryParams = category ? `?category=${category}` : '';
      const url = `${this.baseUrl}/templates${queryParams}`;


      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Templates Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch templates (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching templates:', error);
      return {
        success: false,
        data: { templates: [], groupedByCategory: {}, total: 0 },
        error: error instanceof Error ? error.message : 'Failed to fetch templates'
      };
    }
  }

  /**
   * ✏️ Update notification template
   * PATCH /api/admin/notifications/templates/:type
   */
  async updateTemplate(
    type: string,
    updates: UpdateTemplateRequest
  ): Promise<{
    success: boolean;
    message?: string;
    data?: NotificationTemplate;
    error?: string;
  }> {
    try {
      const url = `${this.baseUrl}/templates/${type}`;


      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Update Template Error ${response.status}:`, errorText);

        let errorMessage = `Failed to update template (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      return {
        success: result.success,
        message: result.message || 'Template updated successfully',
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error updating template:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update template'
      };
    }
  }

  /**
   * 📤 Send custom notification
   * POST /api/admin/notifications/send
   */
  async sendNotification(request: Omit<SendNotificationRequest, 'adminUserId'>): Promise<SendNotificationResponse> {
    try {
      const adminUserId = this.getAdminUserId();
      const url = `${this.baseUrl}/send`;

      const requestBody = {
        ...request,
        adminUserId
      };


      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Send Notification Error ${response.status}:`, errorText);

        let errorMessage = `Failed to send notification (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          message: errorMessage,
          data: { sent: 0, failed: 0, total: 0, sentBy: adminUserId, sentAt: new Date().toISOString() },
          error: errorMessage
        };
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error sending notification:', error);
      const adminUserId = this.getAdminUserId();
      return {
        success: false,
        message: 'Failed to send notification',
        data: { sent: 0, failed: 0, total: 0, sentBy: adminUserId, sentAt: new Date().toISOString() },
        error: error instanceof Error ? error.message : 'Failed to send notification'
      };
    }
  }

  /**
   * 📤 Send notification to single user (by userId, email, or phoneNumber)
   * POST /api/admin/notifications/send-to-user
   */
  async sendToUser(request: Omit<SendToUserRequest, 'adminUserId'>): Promise<SendToUserResponse> {
    try {
      const adminUserId = this.getAdminUserId();
      const url = `${this.baseUrl}/send-to-user`;

      const requestBody = {
        ...request,
        adminUserId
      };


      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Send To User Error ${response.status}:`, errorText);

        let errorMessage = `Failed to send notification (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          message: errorMessage,
          data: {
            notificationId: '',
            sentTo: { userId: '', name: '' },
            notification: { title: '', message: '', type: '' },
            sentBy: adminUserId,
            sentAt: new Date().toISOString(),
            identifierUsed: 'userId'
          },
          error: errorMessage
        };
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error sending notification to user:', error);
      const adminUserId = this.getAdminUserId();
      return {
        success: false,
        message: 'Failed to send notification',
        data: {
          notificationId: '',
          sentTo: { userId: '', name: '' },
          notification: { title: '', message: '', type: '' },
          sentBy: adminUserId,
          sentAt: new Date().toISOString(),
          identifierUsed: 'userId'
        },
        error: error instanceof Error ? error.message : 'Failed to send notification'
      };
    }
  }

  /**
   * 🔍 Lookup user by phone, email, or name
   * GET /api/admin/notifications/lookup-user
   */
  async lookupUser(query: string): Promise<UserLookupResponse> {
    try {
      const url = `${this.baseUrl}/lookup-user?query=${encodeURIComponent(query)}`;


      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Lookup User Error ${response.status}:`, errorText);
        throw new Error(`Failed to lookup user (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error looking up user:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to lookup user'
      };
    }
  }

  /**
   * 📊 Get notification analytics
   * GET /api/admin/notifications/analytics
   * Optional: ?startDate=2025-10-01&endDate=2025-10-25
   */
  async getAnalytics(startDate?: string, endDate?: string): Promise<NotificationAnalytics> {
    try {
      const queryParams = new URLSearchParams();
      if (startDate) queryParams.append('startDate', startDate);
      if (endDate) queryParams.append('endDate', endDate);

      const url = `${this.baseUrl}/analytics${queryParams.toString() ? `?${queryParams}` : ''}`;


      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Analytics Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch analytics (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching analytics:', error);
      return {
        success: false,
        data: {
          totalSent: 0,
          totalRead: 0,
          readRate: 0,
          byType: {},
          period: {
            startDate: startDate || new Date().toISOString(),
            endDate: endDate || new Date().toISOString()
          }
        },
        error: error instanceof Error ? error.message : 'Failed to fetch analytics'
      };
    }
  }

  /**
   * 📜 Get notification history
   * GET /api/admin/notifications/history
   * Optional: ?page=1&limit=20&type=vehicle_approved&startDate=2025-10-01
   */
  async getHistory(params?: {
    page?: number;
    limit?: number;
    type?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<NotificationHistoryResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.type) queryParams.append('type', params.type);
      if (params?.startDate) queryParams.append('startDate', params.startDate);
      if (params?.endDate) queryParams.append('endDate', params.endDate);

      const url = `${this.baseUrl}/history${queryParams.toString() ? `?${queryParams}` : ''}`;


      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get History Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch history (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching history:', error);
      return {
        success: false,
        data: {
          notifications: [],
          pagination: {
            total: 0,
            page: params?.page || 1,
            limit: params?.limit || 20,
            totalPages: 0,
            hasMore: false
          }
        },
        error: error instanceof Error ? error.message : 'Failed to fetch history'
      };
    }
  }

  /**
   * 📊 Get enhanced notification statistics
   * GET /api/admin/notifications/stats
   */
  async getEnhancedStats(): Promise<{
    success: boolean;
    data?: EnhancedNotificationStats;
    error?: string;
  }> {
    try {
      const url = `${this.baseUrl}/stats`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Enhanced Stats Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch stats (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching enhanced stats:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch stats'
      };
    }
  }

  /**
   * 📋 Get paginated notification list
   * GET /api/admin/notifications/list
   */
  async getNotificationsList(params?: {
    page?: number;
    limit?: number;
    type?: string;
    read?: boolean;
    status?: string;
    userId?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<NotificationListResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.type) queryParams.append('type', params.type);
      if (params?.read !== undefined) queryParams.append('read', params.read.toString());
      if (params?.status) queryParams.append('status', params.status);
      if (params?.userId) queryParams.append('userId', params.userId);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.startDate) queryParams.append('startDate', params.startDate);
      if (params?.endDate) queryParams.append('endDate', params.endDate);

      const url = `${this.baseUrl}/list${queryParams.toString() ? `?${queryParams}` : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Notifications List Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch notifications list (${response.status})`);
      }

      const result = await response.json();

      // Transform backend response to match frontend interface
      if (result.success && result.data?.notifications) {
        result.data.notifications = result.data.notifications.map((notif: any) => {
          // Backend may return user info with different field names
          const transformed = {
            ...notif,
            // Map user fields (backend may use 'name', 'email' directly or in nested 'user' object)
            userName: notif.userName || notif.name || notif.user?.name || notif.user?.fullName,
            userEmail: notif.userEmail || notif.email || notif.user?.email,
            // Ensure userId is set
            userId: notif.userId || notif.user?.id || notif.user?._id,
          };

          return transformed;
        });
      }

      return result;
    } catch (error) {
      console.error('❌ Error fetching notifications list:', error);
      return {
        success: false,
        data: {
          notifications: [],
          summary: {
            total: 0,
            read: 0,
            unread: 0,
            readRate: 0
          },
          pagination: {
            page: params?.page || 1,
            limit: params?.limit || 50,
            total: 0,
            totalPages: 0,
            hasMore: false
          }
        },
        error: error instanceof Error ? error.message : 'Failed to fetch notifications list'
      };
    }
  }

  /**
   * 📋 Get notifications for a specific booking
   * GET /api/admin/notifications?bookingId=xxx
   */
  async getNotificationsByBooking(bookingId: string): Promise<NotificationListResponse> {
    try {
      const url = `${this.baseUrl}?bookingId=${encodeURIComponent(bookingId)}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Booking Notifications Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch notifications (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching booking notifications:', error);
      return {
        success: false,
        data: {
          notifications: [],
          summary: {
            total: 0,
            read: 0,
            unread: 0,
            readRate: 0
          },
          pagination: {
            page: 1,
            limit: 50,
            total: 0,
            totalPages: 0,
            hasMore: false
          }
        },
        error: error instanceof Error ? error.message : 'Failed to fetch notifications'
      };
    }
  }

  /**
   * ✅ Mark notification as read
   * PUT /api/admin/notifications/mark-read
   */
  async markAsRead(notificationId: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const url = `${this.baseUrl}/mark-read`;


      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notificationId })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Mark As Read Error ${response.status}:`, errorText);

        let errorMessage = `Failed to mark notification as read (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      return {
        success: result.success,
        message: result.message || 'Notification marked as read'
      };
    } catch (error) {
      console.error('❌ Error marking notification as read:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to mark notification as read'
      };
    }
  }

  /**
   * 🔄 Initialize notification templates (one-time setup)
   * POST /api/admin/database/initialize-notification-templates
   */
  async initializeTemplates(): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl()}/api/admin/database/initialize-notification-templates`;


      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Initialize Templates Error ${response.status}:`, errorText);
        throw new Error(`Failed to initialize templates (${response.status})`);
      }

      const result = await response.json();

      return {
        success: result.success,
        message: result.message || 'Templates initialized successfully'
      };
    } catch (error) {
      console.error('❌ Error initializing templates:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to initialize templates'
      };
    }
  }
}

export const adminNotificationService = new AdminNotificationService();
export default adminNotificationService;
